import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, Cookie, Settings, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify(prefs));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = { necessary: true, analytics: false, marketing: false };
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
  };

  const saveSelection = () => {
    savePreferences(preferences);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="glass-card rounded-2xl p-6 md:p-8 shadow-2xl border border-border/50">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Cookie-Einstellungen</h3>
                    <p className="text-sm text-muted-foreground">Wir respektieren Ihre Privatsphäre</p>
                  </div>
                </div>
                <button
                  onClick={acceptNecessary}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Main Content */}
              <div className="mb-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können wählen, welche Cookies Sie akzeptieren möchten. Weitere Informationen finden Sie 
                  in unserer{" "}
                  <Link to="/datenschutz" className="text-primary hover:underline font-medium">
                    Datenschutzerklärung
                  </Link>.
                </p>
              </div>

              {/* Cookie Details */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-4 mb-6 border-t border-border/50 pt-6">
                      {/* Necessary Cookies */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                        <div className="flex items-start gap-3">
                          <Shield className="w-5 h-5 text-primary mt-0.5" />
                          <div>
                            <h4 className="font-medium text-foreground">Notwendige Cookies</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Diese Cookies sind für den Betrieb der Website erforderlich und können nicht deaktiviert werden.
                            </p>
                          </div>
                        </div>
                        <Switch checked={true} disabled className="opacity-50" />
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded bg-secondary flex items-center justify-center mt-0.5">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground">Analyse-Cookies</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Helfen uns zu verstehen, wie Besucher mit der Website interagieren, um sie zu verbessern.
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.analytics}
                          onCheckedChange={(checked) =>
                            setPreferences((prev) => ({ ...prev, analytics: checked }))
                          }
                        />
                      </div>

                      {/* Marketing Cookies */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded bg-accent flex items-center justify-center mt-0.5">
                            <div className="w-2 h-2 rounded-full bg-accent-foreground" />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground">Marketing-Cookies</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Werden verwendet, um Besuchern relevante Werbung und Marketingkampagnen anzuzeigen.
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.marketing}
                          onCheckedChange={(checked) =>
                            setPreferences((prev) => ({ ...prev, marketing: checked }))
                          }
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  variant="outline"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <Settings className="w-4 h-4" />
                  {showDetails ? "Weniger anzeigen" : "Einstellungen anpassen"}
                </Button>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  {showDetails ? (
                    <Button onClick={saveSelection} className="w-full sm:w-auto">
                      Auswahl speichern
                    </Button>
                  ) : (
                    <Button
                      onClick={acceptNecessary}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      Nur Notwendige
                    </Button>
                  )}
                  <Button
                    onClick={acceptAll}
                    className="gradient-mint text-primary-foreground w-full sm:w-auto"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
